import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { fetchSEOPages } from "../../store/slices/seoSlice";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import ProjectsGrid from "./components/ProjectsGrid";
import ProjectsHeader from "./components/ProjectsHeader";
import MobileProjectsGrid from "./components/MobileProjectsGrid";
import Loader from "../../components/base-components/Loader";
import ErrorMessage from "../../components/base-components/Error";

const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 },
};

const Projects: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const {
    seoPages,
    status: seoStatus,
    error: seoError,
  } = useSelector((state: RootState) => state.seoPages);
  const { ref, inView } = useInView({ threshold: 0.1 });

  useEffect(() => {
    if (seoStatus === "idle") {
      dispatch(fetchSEOPages());
    }
  }, [dispatch, seoStatus]);

  if (seoStatus === "loading") {
    return <Loader />;
  }

  if (seoStatus === "failed") {
    return <ErrorMessage message={seoError} />;
  }

  const getSEOData = () => {
    return seoPages.find(
      (page) => `/${page.attributes.url}` === location.pathname
    )?.attributes;
  };

  const seo = getSEOData();

  return (
    <>
      <Helmet>
        <title>{seo?.seo_title}</title>
        {seo?.seo_description && (
          <meta name="description" content={seo.seo_description} />
        )}

        <link rel="canonical" href={window.location.href} />
        <meta
          property="og:image"
          content="https://www.dancap-group.co.uk/images/dancap-logo-dark.jpg"
        />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta property="og:title" content={seo?.seo_title} />
        <meta property="og:description" content={seo?.seo_description} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://www.dancap-group.co.uk/images/dancap-logo-dark.jpg"
        />
      </Helmet>
      <motion.div
        ref={ref}
        className="h-full flex flex-col"
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={sectionVariants}
        transition={{ duration: 0.8, ease: "easeOut", staggerChildren: 0.2 }}
      >
        <ProjectsHeader />
        <div className="md:hidden flex-1">
          <MobileProjectsGrid />
        </div>
        <div className="hidden md:block flex-1">
          <ProjectsGrid />
        </div>
      </motion.div>
    </>
  );
};

export default Projects;
